import prepareTranslate from '@/helpers/dictionary';
import { useForm } from '@mantine/form';
import {
  Grid,
  NumberInput,
  MultiSelect,
  Select,
  Button,
  TextInput,
  Textarea,
} from '@mantine/core';
import agencyCommissionsMutation from '@/api/mutitations/agencyCommissions';
import agencyCommissionsQuery from '@/api/queries/agencyCommissions';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import companyQuery from '@/api/queries/company';
import { useEffect, useState } from 'react';
import routes from '@/routes';
import productQuery from '@/api/queries/product';
import validations from '@/helpers/validation';
import DetailsModal from '@/components/DetailsModal';
import { MonthPickerInput } from '@mantine/dates';
import { YES_NO } from '@/helpers/enums';
import s3Mutations from '@/api/mutitations/s3';
import { useDisclosure } from '@mantine/hooks';
import Modal from '../Modal/Index';
import EditFiles from '../EditFiles';
import UploadFilesButton from '../uploadFileButton';
import TieredCommissionInput from '../TieredCommissionInput';

function AgencyCommissionsEdit(props) {
  const {
    open,
    close,
    opened,
    id,
    setKey,
  } = props;

  const t = prepareTranslate();
  const navigate = useNavigate();

  const { useUpload } = s3Mutations;
  const { mutateAsync: uploadMutate } = useUpload();

  const [loading, setLoading] = useState(false);

  const {
    data: agencyCommissionData,
    status,
    refetch,
  } = agencyCommissionsQuery.useGetAgencyCommissionsById({ id });

  useEffect(() => {
    if (opened && id) {
      refetch();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, id]);

  const [actionResultOpended,
    { open: actionResultOpen, close: actionResultClose }] = useDisclosure();

  const resultActions = [
    <Button key='closeButton' onClick={() => actionResultClose()}>{t('global.close')}</Button>,
  ];

  const [resultLabel, setResultLabel] = useState('');

  const { data: companiesData } = companyQuery.useGetAll();
  const { data: productsData } = productQuery.useGetAll();

  const form = useForm({
    initialValues: {
      company: '',
      product: [],
      agencyFeeMonthlyDeposit: '',
      agencyFeeMonthlyAccumulation: '',
      agencyFeeAnnualAccumulation: [{ from: 0, to: null, rate: 0 }],
      agencyFeeAnnualDeposit: [{ from: 0, to: null, rate: 0 }],
      files: [],
      comments: '',
      payeeFeesValidityDate: null,
      volumeFeesValidityDate: null,
    },
    validate: {
      company: (value) => validations.required(value),
      product: (value) => validations.required(value),
      payeeFeesStartDate: (value) => validations.required(value),
      payeeFeesValidityDate: (value, values) => {
        if (!value || !values.payeeFeesStartDate) return null;
        return value > values.payeeFeesStartDate ? null : 'תאריך תוקף חייב להיות אחרי תאריך ההתחלה';
      },
      volumeFeesStartDate: (value) => validations.required(value),
      volumeFeesValidityDate: (value, values) => {
        if (!value || !values.volumeFeesStartDate) return null;
        return value > values.volumeFeesStartDate ? null : 'תאריך תוקף חייב להיות אחרי תאריך ההתחלה';
      },
      agencyFeeAnnualDeposit: (tiers) => {
        if (!Array.isArray(tiers) || tiers.length <= 1) return null;
        for (let i = 1; i < tiers.length; i += 1) {
          if ((tiers[i]?.rate ?? 0) <= (tiers[i - 1]?.rate ?? 0)) {
            return t('components.tieredCommissionInput.rateError');
          }
        }
        return null;
      },
      agencyFeeAnnualAccumulation: (tiers) => {
        if (!Array.isArray(tiers) || tiers.length <= 1) return null;
        for (let i = 1; i < tiers.length; i += 1) {
          if ((tiers[i]?.rate ?? 0) <= (tiers[i - 1]?.rate ?? 0)) {
            return t('components.tieredCommissionInput.rateError');
          }
        }
        return null;
      },
    },
  });

  const companiesOptions = companiesData ? (
    companiesData.map((company) => ({
      label: company.hebrew,
      value: company._id,
    }))) : (
    []
  );

  const productsOptions = productsData ? (
    productsData.map((product) => ({
      label: product.hebrew,
      value: product._id,
    }))) : (
    []
  );

  const isValidDate = (date) => date && !Number.isNaN(new Date(date).getTime());

  useEffect(() => {
    if (!agencyCommissionData && status !== 'pending') {
      navigate(routes.agencyCommissions.path);
    }

    form.setValues({
      company: agencyCommissionData?.company?._id ?? '',
      product: agencyCommissionData?.product?.map((item) => item._id) ?? [],
      agencyFeeMonthlyDeposit: agencyCommissionData?.agencyFeeMonthlyDeposit ?? '',
      agencyFeeMonthlyAccumulation: agencyCommissionData?.agencyFeeMonthlyAccumulation ?? '',
      agencyFeeAnnualAccumulation: Array.isArray(agencyCommissionData?.agencyFeeAnnualAccumulation)
        ? agencyCommissionData.agencyFeeAnnualAccumulation
        : [{ from: 0, to: null, rate: 0 }],
      agencyFeeAnnualDeposit: Array.isArray(agencyCommissionData?.agencyFeeAnnualDeposit)
        ? agencyCommissionData.agencyFeeAnnualDeposit
        : [{ from: 0, to: null, rate: 0 }],
      agentNumber: agencyCommissionData?.agentNumber ?? '',
      payeeFeesStartDate: isValidDate(agencyCommissionData?.payeeFeesStartDate)
        ? new Date(agencyCommissionData.payeeFeesStartDate)
        : null,
      isPayeeFeesVatIncluded: agencyCommissionData?.isPayeeFeesVatIncluded ? 'yes' : 'no',
      volumeFeesStartDate: isValidDate(agencyCommissionData?.volumeFeesStartDate)
        ? new Date(agencyCommissionData.volumeFeesStartDate)
        : null,
      isVolumeFeesVatIncluded: agencyCommissionData?.isVolumeFeesVatIncluded ? 'yes' : 'no',
      files: agencyCommissionData?.files || [],
      comments: agencyCommissionData?.comments || '',
      payeeFeesValidityDate: isValidDate(agencyCommissionData?.payeeFeesValidityDate)
        ? new Date(agencyCommissionData.payeeFeesValidityDate)
        : null,
      volumeFeesValidityDate: isValidDate(agencyCommissionData?.volumeFeesValidityDate)
        ? new Date(agencyCommissionData.volumeFeesValidityDate)
        : null,
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyCommissionData, status]);

  const { mutateAsync: agencyCommissionsMutate } = agencyCommissionsMutation.useUpdateOne();
  const { mutateAsync: agencyCommissionsCreate } = agencyCommissionsMutation.useCreateOne();

  const buildArchivedData = (original) => ({
    id,
    company: original?.company?._id,
    product: original?.product?.map((p) => p._id),
    agencyFeeMonthlyDeposit: original?.agencyFeeMonthlyDeposit,
    agencyFeeMonthlyAccumulation: original?.agencyFeeMonthlyAccumulation,
    agencyFeeAnnualDeposit: original?.agencyFeeAnnualDeposit,
    agencyFeeAnnualAccumulation: original?.agencyFeeAnnualAccumulation,
    agentNumber: original?.agentNumber,
    payeeFeesStartDate: original?.payeeFeesStartDate,
    payeeFeesValidityDate: original?.payeeFeesValidityDate,
    volumeFeesStartDate: original?.volumeFeesStartDate,
    volumeFeesValidityDate: original?.volumeFeesValidityDate,
    isPayeeFeesVatIncluded: original?.isPayeeFeesVatIncluded,
    isVolumeFeesVatIncluded: original?.isVolumeFeesVatIncluded,
    files: original.files,
    comments: original?.comments,
    status: 'archived',
  });

  const handleAgencyCommissionUpdateClick = async (values) => {
    setLoading(true);
    let files;
    try {
      const newFiles = form.values.files?.filter((file) => !file.path);

      const clonedValues = { ...values };

      if (newFiles?.length > 0) {
        const formData = new FormData();

        newFiles.forEach((file) => {
          const realFile = file.file;
          formData.append('files', realFile, file.name);
        });

        try {
          files = await uploadMutate(formData);
          const oldFiles = form.values.files.filter((file) => file.path);
          clonedValues.files = [...files.files, ...oldFiles];
        } catch (error) {
          setResultLabel(t('pages.agentEdit.notification.failure.title'));
          actionResultOpen();
          return;
        }
      }

      clonedValues.isPayeeFeesVatIncluded = clonedValues.isPayeeFeesVatIncluded === 'yes';
      clonedValues.isVolumeFeesVatIncluded = clonedValues.isVolumeFeesVatIncluded === 'yes';
      const {
        payeeFeesStartDate,
        volumeFeesStartDate,
      } = clonedValues;

      const origPayeeTime = agencyCommissionData.payeeFeesStartDate
        ? new Date(agencyCommissionData.payeeFeesStartDate).getTime()
        : null;
      const origVolumeTime = agencyCommissionData.volumeFeesStartDate
        ? new Date(agencyCommissionData.volumeFeesStartDate).getTime()
        : null;
      const datesChanged = (
        payeeFeesStartDate ? new Date(payeeFeesStartDate).getTime() : null) !== origPayeeTime
        || (
          volumeFeesStartDate ? new Date(volumeFeesStartDate).getTime() : null) !== origVolumeTime;

      const commissionsChanged = Number(agencyCommissionData.agencyFeeMonthlyDeposit) !== Number(
        clonedValues.agencyFeeMonthlyDeposit,
      ) || Number(agencyCommissionData.agencyFeeMonthlyAccumulation) !== Number(
        clonedValues.agencyFeeMonthlyAccumulation,
      ) || JSON.stringify(agencyCommissionData.agencyFeeAnnualDeposit) !== JSON.stringify(
        clonedValues.agencyFeeAnnualDeposit,
      ) || JSON.stringify(agencyCommissionData.agencyFeeAnnualAccumulation) !== JSON.stringify(
        clonedValues.agencyFeeAnnualAccumulation,
      );

      const needToArchive = datesChanged && commissionsChanged;

      if (!needToArchive) {
        const updateValues = {
          ...clonedValues,
          id,
        };

        await agencyCommissionsMutate(updateValues);

        setResultLabel(t('pages.agencyCommissionsEdit.notification.success.title'));
        actionResultOpen();
        close();
        setKey((prev) => prev + 1);
        return;
      }

      const oldRef = new Date(
        agencyCommissionData.payeeFeesStartDate || agencyCommissionData.volumeFeesStartDate,
      );
      const newIsEarlier = (payeeFeesStartDate && new Date(payeeFeesStartDate) < oldRef)
        || (volumeFeesStartDate && new Date(volumeFeesStartDate) < oldRef);

      if (newIsEarlier) {
        // New record has earlier dates — archive the new one, keep old active
        const created = await agencyCommissionsCreate({
          ...clonedValues,
          files: files ? files.files : form.values.files.filter((file) => file.path),
          status: 'archived',
          archivedAt: new Date().toISOString(),
        });

        if (!created) {
          setResultLabel(t('pages.agencyCommissionsEdit.notification.failure.title'));
          actionResultOpen();
          form.errors = null;
          return;
        }
      } else {
        // New record has later or equal dates — archive old, create new active
        const archived = {
          ...buildArchivedData(agencyCommissionData),
          status: 'archived',
          archivedAt: new Date().toISOString(),
          payeeFeesValidityDate: agencyCommissionData.payeeFeesValidityDate || payeeFeesStartDate,
          volumeFeesValidityDate:
            agencyCommissionData.volumeFeesValidityDate || volumeFeesStartDate,
        };

        const editedAgencyCommissions = await agencyCommissionsMutate({ ...archived, id });

        const fresh = {
          ...clonedValues,
          files: files ? files.files : form.values.files.filter((file) => file.path),
          status: 'active',
        };

        const created = await agencyCommissionsCreate(fresh);

        if (!editedAgencyCommissions || !created) {
          setResultLabel(t('pages.agencyCommissionsEdit.notification.failure.title'));
          actionResultOpen();
          form.errors = null;
          return;
        }
      }

      setResultLabel(t('pages.agencyCommissionsEdit.notification.success.title'));
      close();
      actionResultOpen();
      setKey((prev) => prev + 1);
    } catch (error) {
      setResultLabel(t('pages.agencyCommissionsEdit.notification.failure.title'));
      actionResultOpen();
      form.errors = null;
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    form.errors = null;
    close();
  };

  const onFileSelected = (files) => {
    const formattedFiles = files.map((file) => ({ file, name: file.name }));
    form.setFieldValue('files', [...form.values.files, ...formattedFiles]);
  };

  const actions = [
    <Button
      key='primary'
      loading={loading}
      w={'7rem'}
      onClick={form.onSubmit(handleAgencyCommissionUpdateClick)}
    >
      {t('global.update')}
    </Button>,
    <Button key='cancel' w={'7rem'} variant='outline' onClick={handleCancelClick}>
      {t('global.cancel')}
    </Button>,
  ];

  const onFileNameChange = (index, newName) => {
    const updated = [...form.values.files];
    updated[index] = { ...updated[index], name: newName };
    form.setFieldValue('files', updated);
  };

  const onDeleteFile = (index) => {
    form.removeListItem('files', index);
  };

  const payeeFeesMinDate = form.values.payeeFeesStartDate
    ? new Date(
      form.values.payeeFeesStartDate.getFullYear(),
      form.values.payeeFeesStartDate.getMonth() + 1,
      1,
    ) : undefined;

  const volumeFeesMinDate = form.values.volumeFeesStartDate
    ? new Date(
      form.values.volumeFeesStartDate.getFullYear(),
      form.values.volumeFeesStartDate.getMonth() + 1,
      1,
    ) : undefined;

  return (
    <>
    <DetailsModal
        actions={actions}
        open={open}
        close={handleCancelClick}
        opened = {opened}
        label={t('pages.agencyCommissionsEdit.title')}
        size='90%'
      >
        <Grid gutter='lg' mb={'1rem'}>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <Select
              label={t('pages.agencyCommissionsCreate.form.company')}
              data={companiesOptions}
              required
              key={form.key('company')}
              {...form.getInputProps('company')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <MultiSelect
              label={t('pages.agencyCommissionsCreate.form.product')}
              data={productsOptions}
              required
              key={form.key('product')}
              {...form.getInputProps('product')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <TextInput
              label={t('pages.agencyCommissionsCreate.form.agentNumber')}
              key={form.key('agentNumber')}
              {...form.getInputProps('agentNumber')}
            />
          </Grid.Col>
        </Grid>
        <Grid gutter='lg' mb={'1rem'}>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <MonthPickerInput
              label={t('pages.agencyCommissionsCreate.form.payeeFeesStartDate')}
              key={form.key('payeeFeesStartDate')}
              required
              {...form.getInputProps('payeeFeesStartDate')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <MonthPickerInput
              label={t('pages.agencyCommissionsCreate.form.payeeFeesValidityDate')}
              key={form.key('payeeFeesValidityDate')}
              minDate={payeeFeesMinDate}
              {...form.getInputProps('payeeFeesValidityDate')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <NumberInput
              label={t('pages.agencyCommissionsCreate.form.agencyFeeMonthlyDeposit')}
              key={form.key('agencyFeeMonthlyDeposit')}
              maxLength={5}
              min={0}
              defaultValue={0}
              {...form.getInputProps('agencyFeeMonthlyDeposit')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <NumberInput
              label={t('pages.agencyCommissionsCreate.form.agencyFeeMonthlyAccumulation')}
              key={form.key('agencyFeeMonthlyAccumulation')}
              maxLength={5}
              min={0}
              defaultValue={0}
              {...form.getInputProps('agencyFeeMonthlyAccumulation')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <Select
              label={t('pages.agencyCommissionsCreate.form.isPayeeFeesVatIncluded')}
              data={YES_NO}
              key={form.key('isPayeeFeesVatIncluded')}
              {...form.getInputProps('isPayeeFeesVatIncluded')}
            />
          </Grid.Col>
        </Grid>
        <Grid gutter='lg' mb={'1rem'}>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <MonthPickerInput
              label={t('pages.agencyCommissionsCreate.form.volumeFeesStartDate')}
              key={form.key('volumeFeesStartDate')}
              required
              {...form.getInputProps('volumeFeesStartDate')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <MonthPickerInput
              label={t('pages.agencyCommissionsCreate.form.volumeFeesValidityDate')}
              key={form.key('volumeFeesValidityDate')}
              minDate={volumeFeesMinDate}
              {...form.getInputProps('volumeFeesValidityDate')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <Select
              label={t('pages.agencyCommissionsCreate.form.isVolumeFeesVatIncluded')}
              data={YES_NO}
              key={form.key('isVolumeFeesVatIncluded')}
              {...form.getInputProps('isVolumeFeesVatIncluded')}
            />
          </Grid.Col>
        </Grid>

        <Grid gutter='lg' mb={'1rem'}>
          <Grid.Col span={12}>
            <TieredCommissionInput
              label={t('pages.agencyCommissionsCreate.form.agencyFeeAnnualDeposit')}
              {...form.getInputProps('agencyFeeAnnualDeposit')}
            />
          </Grid.Col>
        </Grid>
        <Grid gutter='lg' mb={'1rem'}>
          <Grid.Col span={12}>
            <TieredCommissionInput
              label={t('pages.agencyCommissionsCreate.form.agencyFeeAnnualAccumulation')}
              {...form.getInputProps('agencyFeeAnnualAccumulation')}
            />
          </Grid.Col>
        </Grid>
        <Grid gutter='lg' mb={'1rem'}>
          <Grid.Col span={{ base: 2.2, md: 2.2 }}>
            <Textarea
              label={t('pages.agencyCommissionsCreate.form.comments')}
              key={form.key('comments')}
              minRows={3}
              {...form.getInputProps('comments')}
            />
          </Grid.Col>
        </Grid>

        {form.values.files?.length > 0 && (
  <EditFiles
    files={form.values.files}
    onNameChange={onFileNameChange}
    onRemove={onDeleteFile}
  />
        )}
     <UploadFilesButton
          onFilesSelected={onFileSelected}
          label={t('pages.agencyCommissionsCreate.form.uploadFiles')}
        />

      </DetailsModal>
      <Modal
        opened={actionResultOpended}
        onClose={actionResultClose}
        actions={resultActions}
        label={resultLabel}
      />
    </>
  );
}

AgencyCommissionsEdit.propTypes = {
  open: PropTypes.func,
  close: PropTypes.func,
  opened: PropTypes,
  id: PropTypes.string,
  setKey: PropTypes.func,
};
export default AgencyCommissionsEdit;
